// Garment stages for multi-garment templates.
//
// WHY THIS EXISTS. A customiser "spot" carries a placement slug and nothing
// else: it is never bound to a garment, and `garmentResolution: "fixed"` does
// not filter placements by garment type (see app/routes/api.builder.config.tsx).
// That is exactly what makes a one-product / three-garment bundle possible, but
// it also means a template like the Father's Day Gift Box renders five spots in
// one undifferentiated list with a single "+ Add another spot" button. Nothing
// tells the customer that the first spots are the hoodie, or that the cap and
// the socks still need doing. The owner's words on 2026-07-22 were "it makes no
// sense" and "it's so messy".
//
// There is no DB field for garment grouping and adding one means a schema
// change plus a resolver change plus admin UI. This table is the cheap version:
// a template tag maps to an ordered list of stages, each owning a set of
// placement slugs. The customiser renders one titled block per stage and scopes
// the placement picker to that stage's slugs, so the flow reads
// hoodie -> cap -> socks.
//
// KEEP IN SYNC WITH THE TEMPLATE. The slugs below must match the
// TemplatePlacement rows seeded by scripts/reshape-fd-bundle.ts. A slug listed
// here that the template does not carry is ignored; a template slug missing
// from every stage falls into the trailing "extras" bucket rather than
// disappearing, so a placement added in the admin can never become invisible.

export type Stage = {
  key: string;
  /** Shown as the block heading, already numbered. */
  title: string;
  /** One line under the heading. Keep it concrete. */
  hint: string;
  /** Placement slugs this stage owns, in picker order. */
  slugs: string[];
  /** CTA on the empty stage. */
  cta: string;
  /** CTA once the stage already has a spot. Names what is left to add. */
  ctaMore?: string;
  /** Hard cap on spots for this garment. The cap takes a year and the socks
   *  take one word; offering "+ Add another spot" there invited a second spot
   *  on a panel that physically holds one. Omit for no limit. */
  maxSpots?: number;
  /** Slugs of CustomField rows (placement "in_stage") this stage owns and
   *  renders INSIDE its own block, in this order.
   *
   *  This is how a garment's own colour lives with that garment. The gift box
   *  spends all three Shopify option slots on hoodie size, hoodie colour and
   *  sock size, so the cap's 8 colours and the socks' 16 have nowhere else to
   *  go: without these the customer cannot choose them and nobody knows what
   *  to pack. A slug listed here that the template does not carry is ignored,
   *  same rule as `slugs`. */
  fieldSlugs?: string[];
  /** A REAL Shopify option axis this stage owns, by exact option name (e.g.
   *  "Hoodie colour"). Rendered as a swatch grid inside the stage block that
   *  drives the theme's own variant radio.
   *
   *  Deliberately NOT a CustomField. Cap and sock colour are pure metadata
   *  with no option slot left, so they ride as line properties. Hoodie colour
   *  IS option2 of the 72-variant grid, so it must keep selecting a real
   *  variant: the theme stays the single source of truth for price, stock and
   *  the sold-out pre-order path, and this grid just tells it what to pick. */
  variantAxis?: string;
  /** 2026-07-24: per-placement size locks, keyed by slug. A slug listed here
   *  renders NO size substep; the design stitches at this mm, shown as a
   *  read-only line. Implements the owner's dead-question rule ("if its size
   *  is locked, do not ask 'how big'"): the cap-back year on a 50mm panel is
   *  not a size decision, and the substep demanding one was measured as two
   *  taps plus a 684px auto-scroll on a phone. Values mirror the standalone
   *  products' owner-approved locked templates (rk-est-year 50mm,
   *  rk-dad-socks 60mm). Same reasoning as this table itself: a DB column
   *  means schema + resolver + admin UI, and this is the cheap version. */
  lockedSizeMmBySlug?: Record<string, number>;
};

const STAGES_BY_TAG: Record<string, Stage[]> = {
  "rk-fd-bundle": [
    {
      key: "hoodie",
      title: "1. The hoodie",
      hint: "Their drawing on the chest. Add a word on the sleeve too, it's included.",
      slugs: ["centre", "left-chest", "cuff"],
      cta: "Design the hoodie",
      ctaMore: "+ Add the artist's name on the sleeve",
      variantAxis: "Hoodie colour",
    },
    {
      key: "cap",
      title: "2. The cap",
      hint: "The year he became a dad, stitched on the back panel.",
      slugs: ["hat-back"],
      cta: "Add the year",
      maxSpots: 1,
      fieldSlugs: ["cap-colour"],
      lockedSizeMmBySlug: { "hat-back": 50 },
    },
    {
      key: "socks",
      title: "3. The socks",
      // The socks carry the EST. year too, same as the cap: the collection
      // hero sells them as "BEST DAD EST, the year he became the best dad".
      // This used to ask for "a short word on the cuff", which contradicted
      // the offer the customer had just clicked.
      hint: "The year he became a dad, stitched on the cuff.",
      slugs: ["outside-of-sock"],
      cta: "Add the year",
      maxSpots: 1,
      fieldSlugs: ["sock-colour"],
      lockedSizeMmBySlug: { "outside-of-sock": 60 },
    },
  ],
};

export function stagesFor(templateTag?: string | null): Stage[] | null {
  if (!templateTag) return null;
  return STAGES_BY_TAG[templateTag] ?? null;
}

/** The stage that owns this placement slug, or null when nothing claims it. */
export function stageOfSlug(stages: Stage[], slug: string): Stage | null {
  for (const s of stages) if (s.slugs.includes(slug)) return s;
  return null;
}

/** The stage-table size lock for a placement slug, or null when the slug is
 *  unlocked (or the template has no stages). Callers treat a non-null value
 *  exactly like the template-wide product.lockedSizeMm, but scoped to one
 *  placement. */
export function stageLockedSizeMm(
  templateTag: string | undefined | null,
  slug: string | null | undefined,
): number | null {
  if (!slug) return null;
  const stages = stagesFor(templateTag);
  if (!stages) return null;
  for (const s of stages) {
    const mm = s.lockedSizeMmBySlug?.[slug];
    if (mm) return mm;
  }
  return null;
}

/** Slugs present on the template but claimed by no stage. Rendered last so an
 *  admin-added placement is never silently unreachable. */
export function unstagedSlugs(stages: Stage[], allSlugs: string[]): string[] {
  const claimed = new Set(stages.flatMap((s) => s.slugs));
  return allSlugs.filter((s) => !claimed.has(s));
}

/** Templates whose OFFER is "the year he became a dad" stitched as fixed
 *  BEST-DAD/EST. art. Only these get the four-digit YearBoxes input.
 *
 *  2026-08-03 (Oscar): the YearBoxes used to key on

/** Template tags whose sock-cuff placement is the fixed "BEST DAD / EST.
 *  <year>" art (the Best Dad socks + the Father's Day gift box). On any OTHER
 *  product the same `outside-of-sock` slug is a normal free-text spot, so the
 *  year control must NOT key on the slug alone.
 *
 *  rk-dad-socks   -> the standalone Best Dad EST. socks
 *  rk-fd-bundle   -> the gift box's sock stage (see STAGES_BY_TAG above) */
const BEST_DAD_SOCK_TAGS = new Set(["rk-dad-socks", "rk-fd-bundle"]);

/** True when this template's sock cuff is the locked BEST DAD / EST. year,
 *  rather than an ordinary text spot. Gates the YearBoxes control so the plain
 *  CUSTOM Socks product (tag RK_REGULAR) shows a normal text field. */
export function isBestDadSockTemplate(
  templateTag: string | undefined | null,
): boolean {
  return !!templateTag && BEST_DAD_SOCK_TAGS.has(templateTag);
}
